import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, Play, Users, Copy, PhoneOff, Pencil, Eraser,
  Square, Circle, Minus, Type, MessageSquare, FileText,
  PenTool, Plus, Send, Trash2, Monitor
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const getHeaders = () => {
  const token = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
};

// ===================== LOBBY =====================
function SessionLobby({ onJoinSession }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showJoin, setShowJoin] = useState(false);
  const [name, setName] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const isTeacher = ['super_admin', 'org_admin', 'faculty'].includes(user?.role);

  useEffect(() => {
    fetch(`${API}/api/live-classroom/sessions`, { headers: getHeaders() })
      .then(r => r.json())
      .then(d => { setSessions(d.sessions || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const handleCreate = async () => {
    if (!name.trim()) return toast.error('Session name required');
    try {
      const res = await fetch(`${API}/api/live-classroom/sessions`, {
        method: 'POST', headers: getHeaders(), body: JSON.stringify({ name })
      });
      const raw = await res.text();
      let session; try { session = raw ? JSON.parse(raw) : null; } catch { session = null; }
      if (!res.ok) throw new Error(session?.detail || `Failed (HTTP ${res.status})`);
      setShowCreate(false);
      setName('');
      onJoinSession(session.session_id);
    } catch (e) { toast.error(e.message); }
  };

  const handleJoin = async () => {
    if (!joinCode.trim()) return toast.error('Enter join code');
    try {
      const res = await fetch(`${API}/api/live-classroom/sessions/join`, {
        method: 'POST', headers: getHeaders(), body: JSON.stringify({ join_code: joinCode })
      });
      const raw = await res.text();
      let data; try { data = raw ? JSON.parse(raw) : null; } catch { data = null; }
      if (!res.ok) throw new Error(data?.detail || `Invalid code (HTTP ${res.status})`);
      setShowJoin(false);
      setJoinCode('');
      onJoinSession(data.session_id);
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="live-classroom-lobby">
      <header className="sticky top-0 z-30 border-b border-border bg-background/95 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/classroom')} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <Monitor className="w-6 h-6 text-indigo-500" />
            <h1 className="text-xl font-bold text-foreground">Live Classroom</h1>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowJoin(true)} data-testid="join-code-btn">
              <Users className="w-4 h-4 mr-1" /> Join with Code
            </Button>
            {isTeacher && (
              <Button onClick={() => setShowCreate(true)} data-testid="create-session-btn">
                <Plus className="w-4 h-4 mr-1" /> Start Session
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {loading ? (
          <div className="grid md:grid-cols-3 gap-4">{[1,2,3].map(i => <div key={i} className="h-40 bg-muted animate-pulse rounded-xl" />)}</div>
        ) : sessions.length === 0 ? (
          <div className="text-center py-20" data-testid="empty-state">
            <Monitor className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-2">No active sessions</h2>
            <p className="text-muted-foreground mb-6">
              {isTeacher ? 'Start a live classroom session with video, whiteboard, and shared notes.' : 'Ask your teacher for a join code to enter a live session.'}
            </p>
            {isTeacher && <Button onClick={() => setShowCreate(true)}>Start a Session</Button>}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sessions.map(s => (
              <div key={s.session_id} onClick={() => onJoinSession(s.session_id)}
                className="rounded-xl border border-border bg-card hover:shadow-lg transition-all cursor-pointer overflow-hidden group"
                data-testid={`session-card-${s.session_id}`}>
                <div className="h-24 bg-gradient-to-br from-indigo-500/10 to-violet-500/10 flex items-center justify-center relative">
                  <Monitor className="w-10 h-10 text-indigo-500 group-hover:scale-110 transition-transform" />
                  <span className="absolute top-2 right-2 text-[10px] bg-green-100 dark:bg-green-500/10 text-green-600 dark:text-green-400 px-1.5 py-0.5 rounded font-medium">Live</span>
                </div>
                <div className="p-4">
                  <h3 className="text-sm font-semibold text-foreground truncate mb-1">{s.name}</h3>
                  <p className="text-xs text-muted-foreground mb-2">Host: {s.host_name}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                      <Users className="w-3 h-3" /> {s.participants?.length || 0} joined
                    </span>
                    <span className="text-[10px] font-mono bg-muted px-1.5 py-0.5 rounded">Code: {s.join_code}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent><DialogHeader><DialogTitle>Start Live Session</DialogTitle></DialogHeader>
          <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Physics Chapter 5 - Waves"
            onKeyDown={e => e.key === 'Enter' && handleCreate()} data-testid="session-name-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} data-testid="confirm-create-btn">Start</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Join dialog */}
      <Dialog open={showJoin} onOpenChange={setShowJoin}>
        <DialogContent><DialogHeader><DialogTitle>Join Live Session</DialogTitle></DialogHeader>
          <Input value={joinCode} onChange={e => setJoinCode(e.target.value.toUpperCase())}
            placeholder="Enter 6-character code" maxLength={6}
            onKeyDown={e => e.key === 'Enter' && handleJoin()} data-testid="join-code-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowJoin(false)}>Cancel</Button>
            <Button onClick={handleJoin} data-testid="confirm-join-btn">Join</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ===================== WHITEBOARD PANEL =====================
const WB_TOOLS = [
  { id: 'pen', icon: Pencil, label: 'Pen' },
  { id: 'eraser', icon: Eraser, label: 'Eraser' },
  { id: 'line', icon: Minus, label: 'Line' },
  { id: 'rect', icon: Square, label: 'Rect' },
  { id: 'circle', icon: Circle, label: 'Circle' },
  { id: 'text', icon: Type, label: 'Text' },
];
const WB_COLORS = ['#000000', '#ef4444', '#3b82f6', '#22c55e', '#f59e0b', '#8b5cf6', '#ffffff'];

function WhiteboardPanel({ sessionId, initialStrokes }) {
  const canvasRef = useRef(null);
  const [tool, setTool] = useState('pen');
  const [color, setColor] = useState('#000000');
  const [lineWidth, setLineWidth] = useState(3);
  const [drawing, setDrawing] = useState(false);
  const [strokes, setStrokes] = useState(initialStrokes || []);
  const currentStroke = useRef(null);
  const saveTimer = useRef(null);

  const getPos = useCallback((e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  }, []);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    // Draw white background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    strokes.forEach(s => drawStroke(ctx, s));
  }, [strokes]);

  useEffect(() => { redraw(); }, [redraw]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      const parent = canvas.parentElement;
      canvas.width = parent.clientWidth;
      canvas.height = parent.clientHeight;
      redraw();
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [redraw]);

  const drawStroke = (ctx, s) => {
    if (!s.points || s.points.length < 1) return;
    ctx.save();
    ctx.strokeStyle = s.color || '#000';
    ctx.lineWidth = s.width || 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    if (s.tool === 'eraser') {
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = (s.width || 3) * 4;
    }
    if (s.tool === 'rect' && s.points.length >= 2) {
      const [p0, p1] = [s.points[0], s.points[s.points.length - 1]];
      ctx.strokeRect(p0.x, p0.y, p1.x - p0.x, p1.y - p0.y);
    } else if (s.tool === 'circle' && s.points.length >= 2) {
      const [p0, p1] = [s.points[0], s.points[s.points.length - 1]];
      const rx = Math.abs(p1.x - p0.x) / 2, ry = Math.abs(p1.y - p0.y) / 2;
      ctx.beginPath();
      ctx.ellipse(p0.x + (p1.x - p0.x) / 2, p0.y + (p1.y - p0.y) / 2, rx, ry, 0, 0, Math.PI * 2);
      ctx.stroke();
    } else if (s.tool === 'line' && s.points.length >= 2) {
      ctx.beginPath();
      ctx.moveTo(s.points[0].x, s.points[0].y);
      ctx.lineTo(s.points[s.points.length - 1].x, s.points[s.points.length - 1].y);
      ctx.stroke();
    } else {
      ctx.beginPath();
      ctx.moveTo(s.points[0].x, s.points[0].y);
      s.points.forEach(p => ctx.lineTo(p.x, p.y));
      ctx.stroke();
    }
    ctx.restore();
  };

  const onPointerDown = (e) => {
    setDrawing(true);
    const pos = getPos(e);
    currentStroke.current = { tool, color, width: lineWidth, points: [pos] };
  };

  const onPointerMove = (e) => {
    if (!drawing || !currentStroke.current) return;
    const pos = getPos(e);
    currentStroke.current.points.push(pos);
    const ctx = canvasRef.current.getContext('2d');
    redraw();
    drawStroke(ctx, currentStroke.current);
  };

  const onPointerUp = () => {
    if (currentStroke.current && currentStroke.current.points.length > 1) {
      const newStrokes = [...strokes, currentStroke.current];
      setStrokes(newStrokes);
      scheduleSave(newStrokes);
    }
    currentStroke.current = null;
    setDrawing(false);
  };

  const scheduleSave = (data) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      fetch(`${API}/api/live-classroom/sessions/${sessionId}/whiteboard`, {
        method: 'PUT', headers: getHeaders(),
        body: JSON.stringify({ strokes: data })
      }).catch(() => {});
    }, 2000);
  };

  const clearBoard = () => {
    setStrokes([]);
    scheduleSave([]);
  };

  return (
    <div className="flex flex-col h-full" data-testid="whiteboard-panel">
      <div className="flex items-center gap-1 px-2 py-1.5 border-b border-border bg-muted/30 flex-wrap">
        {WB_TOOLS.map(t => (
          <button key={t.id} onClick={() => setTool(t.id)} title={t.label}
            className={`p-1.5 rounded-md transition-colors ${tool === t.id ? 'bg-indigo-100 dark:bg-indigo-500/20 text-indigo-600' : 'text-muted-foreground hover:bg-muted'}`}>
            <t.icon className="w-4 h-4" />
          </button>
        ))}
        <div className="w-px h-5 bg-border mx-1" />
        {WB_COLORS.map(c => (
          <button key={c} onClick={() => setColor(c)}
            className={`w-5 h-5 rounded-full border-2 transition-transform ${color === c ? 'border-foreground scale-125' : 'border-transparent'}`}
            style={{ backgroundColor: c }} />
        ))}
        <div className="w-px h-5 bg-border mx-1" />
        <select value={lineWidth} onChange={e => setLineWidth(Number(e.target.value))}
          className="text-xs bg-background border border-border rounded px-1 py-0.5">
          <option value={2}>Thin</option><option value={3}>Medium</option><option value={6}>Thick</option><option value={10}>Extra</option>
        </select>
        <button onClick={clearBoard} title="Clear" className="p-1.5 rounded-md text-muted-foreground hover:bg-red-100 hover:text-red-500 ml-auto">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      <div className="flex-1 relative overflow-hidden bg-white">
        <canvas ref={canvasRef}
          onMouseDown={onPointerDown} onMouseMove={onPointerMove} onMouseUp={onPointerUp} onMouseLeave={onPointerUp}
          onTouchStart={onPointerDown} onTouchMove={onPointerMove} onTouchEnd={onPointerUp}
          className="absolute inset-0 cursor-crosshair touch-none" data-testid="whiteboard-canvas" />
      </div>
    </div>
  );
}

// ===================== NOTES PANEL =====================
function NotesPanel({ sessionId, initialNotes }) {
  const [notes, setNotes] = useState(initialNotes || '');
  const saveTimer = useRef(null);

  const handleChange = (val) => {
    setNotes(val);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      fetch(`${API}/api/live-classroom/sessions/${sessionId}/notes`, {
        method: 'PUT', headers: getHeaders(),
        body: JSON.stringify({ notes: val })
      }).catch(() => {});
    }, 1500);
  };

  return (
    <div className="flex flex-col h-full" data-testid="notes-panel">
      <div className="px-3 py-2 border-b border-border bg-muted/30 flex items-center gap-2">
        <FileText className="w-4 h-4 text-muted-foreground" />
        <span className="text-xs font-medium text-muted-foreground">Shared Notes — visible to all participants</span>
      </div>
      <textarea value={notes} onChange={e => handleChange(e.target.value)}
        placeholder="Type shared notes here... all participants can see these notes in real-time."
        className="flex-1 w-full resize-none bg-background p-4 text-sm text-foreground outline-none font-mono leading-relaxed"
        data-testid="shared-notes-textarea" />
    </div>
  );
}

// ===================== CHAT PANEL =====================
function ChatPanel({ sessionId, initialMessages }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState(initialMessages || []);
  const [text, setText] = useState('');
  const bottomRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Poll for new messages every 5s
  useEffect(() => {
    pollRef.current = setInterval(async () => {
      try {
        const res = await fetch(`${API}/api/live-classroom/sessions/${sessionId}`, { headers: getHeaders() });
        if (res.ok) {
          const data = await res.json();
          if (data.chat_messages?.length > messages.length) {
            setMessages(data.chat_messages);
          }
        }
      } catch {}
    }, 5000);
    return () => clearInterval(pollRef.current);
  }, [sessionId, messages.length]);

  const send = async () => {
    if (!text.trim()) return;
    try {
      const res = await fetch(`${API}/api/live-classroom/sessions/${sessionId}/chat`, {
        method: 'POST', headers: getHeaders(),
        body: JSON.stringify({ text })
      });
      if (res.ok) {
        const msg = await res.json();
        setMessages(prev => [...prev, msg]);
        setText('');
      }
    } catch {}
  };

  return (
    <div className="flex flex-col h-full" data-testid="chat-panel">
      <div className="px-3 py-2 border-b border-border bg-muted/30 flex items-center gap-2">
        <MessageSquare className="w-4 h-4 text-muted-foreground" />
        <span className="text-xs font-medium text-muted-foreground">Session Chat</span>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {messages.length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-8">No messages yet</p>
        )}
        {messages.map((m, i) => (
          <div key={m.msg_id || i} className={`flex flex-col ${m.sender_id === user?.user_id ? 'items-end' : 'items-start'}`}>
            <span className="text-[10px] text-muted-foreground mb-0.5">{m.sender_name}</span>
            <div className={`px-3 py-1.5 rounded-lg text-xs max-w-[85%] ${m.sender_id === user?.user_id ? 'bg-indigo-500 text-white' : 'bg-muted text-foreground'}`}>
              {m.text}
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
      <div className="p-2 border-t border-border flex gap-1.5">
        <Input value={text} onChange={e => setText(e.target.value)}
          placeholder="Type a message..." className="text-xs h-8"
          onKeyDown={e => e.key === 'Enter' && send()} data-testid="chat-input" />
        <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={send} data-testid="send-chat-btn">
          <Send className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

// ===================== ACTIVE SESSION VIEW =====================
function ActiveSession({ sessionId, onLeave }) {
  const { user } = useAuth();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rightTab, setRightTab] = useState('whiteboard');
  const [panelWidth, setPanelWidth] = useState(45); // percentage for right panel
  const dragging = useRef(false);

  useEffect(() => {
    fetch(`${API}/api/live-classroom/sessions/${sessionId}`, { headers: getHeaders() })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => { setSession(d); setLoading(false); })
      .catch(() => { toast.error('Session not found'); onLeave(); });
  }, [sessionId, onLeave]);

  const handleEnd = async () => {
    try {
      await fetch(`${API}/api/live-classroom/sessions/${sessionId}/end`, {
        method: 'PUT', headers: getHeaders()
      });
      toast.success('Session ended');
      onLeave();
    } catch { toast.error('Failed to end session'); }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(session?.join_code || '');
    toast.success('Join code copied!');
  };

  // Resize handler
  const onDragStart = (e) => {
    e.preventDefault();
    dragging.current = true;
    const onMove = (ev) => {
      if (!dragging.current) return;
      const clientX = ev.touches ? ev.touches[0].clientX : ev.clientX;
      const pct = ((window.innerWidth - clientX) / window.innerWidth) * 100;
      setPanelWidth(Math.min(65, Math.max(25, pct)));
    };
    const onUp = () => { dragging.current = false; window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); window.removeEventListener('touchmove', onMove); window.removeEventListener('touchend', onUp); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove);
    window.addEventListener('touchend', onUp);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-background"><div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" /></div>;

  const isHost = session?.host_id === user?.user_id;
  const jitsiUrl = `https://meet.jit.si/${session.jitsi_room}#userInfo.displayName="${encodeURIComponent(user?.name || 'User')}"`;

  const tabs = [
    { id: 'whiteboard', label: 'Whiteboard', icon: PenTool },
    { id: 'notes', label: 'Notes', icon: FileText },
    { id: 'chat', label: 'Chat', icon: MessageSquare },
  ];

  return (
    <div className="h-screen flex flex-col bg-background" data-testid="live-session-active">
      {/* Top bar */}
      <header className="h-12 border-b border-border bg-background/95 backdrop-blur flex items-center px-3 gap-3 shrink-0 z-20">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onLeave} data-testid="leave-session-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <Monitor className="w-5 h-5 text-indigo-500" />
        <h1 className="text-sm font-bold text-foreground truncate">{session.name}</h1>
        <span className="text-[10px] bg-green-100 dark:bg-green-500/10 text-green-600 dark:text-green-400 px-1.5 py-0.5 rounded font-medium">Live</span>
        <span className="text-xs text-muted-foreground ml-1">
          <Users className="w-3.5 h-3.5 inline mr-0.5" />{session.participants?.length || 0}
        </span>
        <div className="flex-1" />
        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={copyCode} data-testid="copy-code-btn">
          <Copy className="w-3 h-3 mr-1" /> {session.join_code}
        </Button>
        {isHost && (
          <Button variant="destructive" size="sm" className="h-7 text-xs" onClick={handleEnd} data-testid="end-session-btn">
            <PhoneOff className="w-3 h-3 mr-1" /> End
          </Button>
        )}
      </header>

      {/* Split content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left: Jitsi Meet */}
        <div className="h-full overflow-hidden" style={{ width: `${100 - panelWidth}%` }}>
          <iframe src={jitsiUrl} allow="camera; microphone; display-capture; autoplay; clipboard-write"
            className="w-full h-full border-none" title="Live Video" data-testid="jitsi-iframe" />
        </div>

        {/* Resize handle */}
        <div onMouseDown={onDragStart} onTouchStart={onDragStart}
          className="w-1.5 bg-border hover:bg-indigo-400 cursor-col-resize shrink-0 transition-colors active:bg-indigo-500"
          data-testid="resize-handle" />

        {/* Right: Whiteboard / Notes / Chat */}
        <div className="h-full flex flex-col overflow-hidden" style={{ width: `${panelWidth}%` }}>
          {/* Tabs */}
          <div className="flex border-b border-border shrink-0 bg-muted/20">
            {tabs.map(t => (
              <button key={t.id} onClick={() => setRightTab(t.id)}
                className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors border-b-2 ${
                  rightTab === t.id ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400' : 'border-transparent text-muted-foreground hover:text-foreground'
                }`} data-testid={`tab-${t.id}`}>
                <t.icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            ))}
          </div>
          {/* Tab content */}
          <div className="flex-1 overflow-hidden">
            {rightTab === 'whiteboard' && <WhiteboardPanel sessionId={sessionId} initialStrokes={session.whiteboard_data || []} />}
            {rightTab === 'notes' && <NotesPanel sessionId={sessionId} initialNotes={session.shared_notes || ''} />}
            {rightTab === 'chat' && <ChatPanel sessionId={sessionId} initialMessages={session.chat_messages || []} />}
          </div>
        </div>
      </div>
    </div>
  );
}

// ===================== MAIN COMPONENT =====================
export default function LiveClassroom() {
  const { sessionId: urlSessionId } = useParams();
  const navigate = useNavigate();
  const [activeSessionId, setActiveSessionId] = useState(urlSessionId || null);

  const handleJoin = useCallback((id) => {
    setActiveSessionId(id);
    navigate(`/live-classroom/${id}`, { replace: true });
  }, [navigate]);

  const handleLeave = useCallback(() => {
    setActiveSessionId(null);
    navigate('/live-classroom', { replace: true });
  }, [navigate]);

  if (activeSessionId) {
    return <ActiveSession sessionId={activeSessionId} onLeave={handleLeave} />;
  }

  return <SessionLobby onJoinSession={handleJoin} />;
}
